const BASE_URL = 'https://fotileams.com/api';

const getToken = () => localStorage.getItem('token');

const headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// ── Auto-logout helper ────────────────────────────────────
// Fires a global event that App.js listens to
let networkFailCount = 0;

function triggerLogout(reason) {
  console.warn('Auto-logout triggered:', reason);
  localStorage.removeItem('token');
  localStorage.removeItem('employee');
  localStorage.removeItem('super_token');
  localStorage.removeItem('super_employee');
  sessionStorage.clear();
  window.dispatchEvent(new CustomEvent('app:logout', { detail: { reason } }));
}

// Wrap every fetch call with auto-logout logic
async function apiFetch(url, options = {}) {
  try {
    const res = await fetch(url, options);

    // Token expired or invalid → immediate logout
    if (res.status === 401) {
      triggerLogout('Session expired — please log in again');
      return { error: 'Session expired' };
    }

    // Reset fail counter on success
    networkFailCount = 0;
    return res.json();

  } catch (err) {
    // Network error (packet loss, server down)
    networkFailCount++;
    console.warn(`Network error #${networkFailCount}:`, err.message);

    if (networkFailCount >= 3) {
      // 3 consecutive network failures → logout
      networkFailCount = 0;
      triggerLogout('Connection lost — please log in again');
      return { error: 'Connection lost' };
    }

    return { error: 'Network error — retrying...' };
  }
}

export const api = {
  // ─── AUTH ────────────────────────────────────────────
  login: (employee_id, password) =>
    fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id, password })
    }).then(r => r.json()),

  // ─── ATTENDANCE (Employee) ────────────────────────────
  getMyAttendance: (month, year) =>
    apiFetch(`${BASE_URL}/attendance/me?month=${month}&year=${year}`, { headers: headers() }),

  clockIn: (lat, lng, bssid, ip) =>
    apiFetch(`${BASE_URL}/attendance/clockin`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ lat, lng, bssid, ip })
    }),

  clockOut: () =>
    apiFetch(`${BASE_URL}/attendance/clockout`, {
      method: 'POST', headers: headers()
    }),

  // ─── ATTENDANCE (HR) ──────────────────────────────────
  getAllAttendance: ({ date, location_id, city, employee_id } = {}) => {
    const params = new URLSearchParams();
    if (date)        params.append('date', date);
    if (location_id) params.append('location_id', location_id);
    if (city)        params.append('city', city);
    if (employee_id) params.append('employee_id', employee_id);
    const qs = params.toString();
    return apiFetch(`${BASE_URL}/attendance/${qs ? `?${qs}` : ''}`, { headers: headers() });
  },

  getAttendanceTrend: (days = 30) =>
    apiFetch(`${BASE_URL}/attendance/trend?days=${days}`, { headers: headers() }),

  getDepartmentStats: () =>
    apiFetch(`${BASE_URL}/attendance/departments`, { headers: headers() }),

  getAlerts: () =>
    apiFetch(`${BASE_URL}/attendance/alerts`, { headers: headers() }),

  // ─── LEAVES (Employee) ───────────────────────────────
  getLeaveBalance: () =>
    apiFetch(`${BASE_URL}/leaves/balance`, { headers: headers() }),

  getMyLeaves: (year) =>
    apiFetch(`${BASE_URL}/leaves/me?year=${year}`, { headers: headers() }),

  applyLeave: (data) =>
    apiFetch(`${BASE_URL}/leaves/apply`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify(data)
    }),

  // Cancel an upcoming leave. Works for both the employee (own future leave)
  // and HR — the backend enforces ownership and the future-only rule.
  cancelLeave: (id) =>
    apiFetch(`${BASE_URL}/leaves/${id}/cancel`, {
      method: 'PUT', headers: headers()
    }),

  // ─── LEAVES (HR) ─────────────────────────────────────
  getAllLeaves: ({ status, city, location_id } = {}) => {
    const params = new URLSearchParams();
    if (status)      params.append('status', status);
    if (city)        params.append('city', city);
    if (location_id) params.append('location_id', location_id);
    const qs = params.toString();
    return apiFetch(`${BASE_URL}/leaves/${qs ? `?${qs}` : ''}`, { headers: headers() });
  },

  approveLeave: (id) =>
    apiFetch(`${BASE_URL}/leaves/${id}/approve`, { method: 'PUT', headers: headers() }),

  rejectLeave: (id, rejection_reason) =>
    apiFetch(`${BASE_URL}/leaves/${id}/reject`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify({ rejection_reason })
    }),

  // ─── SALARY (Employee) ───────────────────────────────
  getMySalary: (month, year) =>
    apiFetch(`${BASE_URL}/salary/me?month=${month}&year=${year}`, { headers: headers() }),

  // ─── SALARY (HR) ─────────────────────────────────────
  getAllSalaryReports: ({ month, year, city, location_id } = {}) => {
    const params = new URLSearchParams();
    if (month)       params.append('month', month);
    if (year)        params.append('year', year);
    if (city)        params.append('city', city);
    if (location_id) params.append('location_id', location_id);
    return apiFetch(`${BASE_URL}/salary/reports?${params.toString()}`, { headers: headers() });
  },

  calculateSalary: (employee_id, month, year) =>
    apiFetch(`${BASE_URL}/salary/calculate/${employee_id}?month=${month}&year=${year}`, { headers: headers() }),

  // ─── EMPLOYEES (HR) ──────────────────────────────────
  getAllEmployees: ({ city, location_id, department_id, search } = {}) => {
    const params = new URLSearchParams();
    if (city)          params.append('city', city);
    if (location_id)   params.append('location_id', location_id);
    if (department_id) params.append('department_id', department_id);
    if (search)        params.append('search', search);
    const qs = params.toString();
    return apiFetch(`${BASE_URL}/employees/${qs ? `?${qs}` : ''}`, { headers: headers() });
  },

  getEmployee: (id) =>
    apiFetch(`${BASE_URL}/employees/${id}`, { headers: headers() }),

  createEmployee: (data) =>
    apiFetch(`${BASE_URL}/employees/`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify(data)
    }),

  updateEmployee: (id, data) =>
    apiFetch(`${BASE_URL}/employees/${id}`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify(data)
    }),

  deactivateEmployee: (id) =>
    apiFetch(`${BASE_URL}/employees/${id}`, {
      method: 'DELETE', headers: headers()
    }),

  getMyProfile: () =>
    apiFetch(`${BASE_URL}/employees/me`, { headers: headers() }),

  // ─── DEPARTMENTS ─────────────────────────────────────
  getAllDepartments: () =>
    apiFetch(`${BASE_URL}/departments/`, { headers: headers() }),

  createDepartment: (data) =>
    apiFetch(`${BASE_URL}/departments/`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify(data)
    }),

  updateDepartment: (id, data) =>
    apiFetch(`${BASE_URL}/departments/${id}`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify(data)
    }),

  // ─── LOCATIONS ───────────────────────────────────────
  getAllLocations: ({ city } = {}) => {
    const params = new URLSearchParams();
    if (city) params.append('city', city);
    const qs = params.toString();
    return apiFetch(`${BASE_URL}/locations/${qs ? `?${qs}` : ''}`, { headers: headers() });
  },

  createLocation: (data) =>
    apiFetch(`${BASE_URL}/locations/`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify(data)
    }),

  updateLocation: (id, data) =>
    apiFetch(`${BASE_URL}/locations/${id}`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify(data)
    }),

  changePassword: (current_password, new_password) =>
    apiFetch(`${BASE_URL}/auth/change-password`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ current_password, new_password }),
    }),
};